// AQI colour palette — the only place the band colors are defined.
// Three consumers used to define their own (slightly different) hex ramps
// inline; this is the single source of truth.
//
// US EPA AQI bands: 0–50 good, 51–100 moderate, 101–150 USG, 151–200 unhealthy,
// 201–300 very unhealthy, 301+ hazardous.
//
// Tokens used: --aqi-good, --aqi-mod, --aqi-usg, --aqi-unh, --aqi-vunh,
// --aqi-haz. Defined in styles/tokens.css (added in the same audit pass).

import type { CSSProperties } from "react";

/** AQI band ID — the canonical 6-band EPA classification. */
export type AqiBand = "good" | "moderate" | "usg" | "unhealthy" | "veryUnhealthy" | "hazardous";

/** Map a numeric US AQI to its band. */
export function aqiBand(aqi: number | null | undefined): AqiBand {
  if (aqi == null || !Number.isFinite(aqi)) return "good";
  if (aqi <= 50) return "good";
  if (aqi <= 100) return "moderate";
  if (aqi <= 150) return "usg";
  if (aqi <= 200) return "unhealthy";
  if (aqi <= 300) return "veryUnhealthy";
  return "hazardous";
}

/** CSS var() reference for the band. Use in `style={{ color: ... }}` etc. */
export function aqiColorVar(band: AqiBand): string {
  return `var(--aqi-${bandToSuffix(band)})`;
}

/** Hex fallback (for SVG stroke/fill which can't take var() in some browsers). */
export function aqiColorHex(band: AqiBand): string {
  switch (band) {
    case "good":          return "#22c55e";
    case "moderate":      return "#facc15";
    case "usg":           return "#f97316";
    case "unhealthy":     return "#ef4444";
    case "veryUnhealthy": return "#a855f7";
    case "hazardous":     return "#7f1d1d";
  }
}

function bandToSuffix(b: AqiBand): string {
  switch (b) {
    case "good":          return "good";
    case "moderate":      return "mod";
    case "usg":           return "usg";
    case "unhealthy":     return "unh";
    case "veryUnhealthy": return "vunh";
    case "hazardous":     return "haz";
  }
}

/** Style object for inline use: `style={aqiStyle(aqi)}`. */
export function aqiStyle(aqi: number | null | undefined): CSSProperties {
  return { color: aqiColorVar(aqiBand(aqi)) };
}
